import * as yup from 'yup';
import type {
  ContractCreatePayload,
  ContractOrderProduct,
  ContractServiceTimes,
  ContractServiceConfigOption,
  ContractServiceSla,
  ContractRole,
  ContractDirection,
} from '@/api/contracts';
import type { AttachmentPayload } from '@/api/uploads';

/**
 * The Contract create form's react-hook-form shape. Mirrors the legacy
 * datacentral-client-v4 `Contract/components/New` single-page form over the
 * full `ContractCreatePayload`. The deep SLA rule builder, NDA upload, and the
 * per-product-type pricing matrix (service_configoptions / domain metadata) are
 * later phases — those payload slots stay empty here and are omitted on submit.
 *
 * `customer_id` / `partner_id` / `customer_contact_id` are plain numbers here;
 * `buildPayload` maps this to the `POST /contracts/customers` body verbatim.
 */

/** A single product line — group → product → detail cascade + service times. */
export interface ProductLine {
  product_group_name: string;
  product_id: number;
  product_name: string;
  product_group_code: string;
  template_name: string;
  service_price_model: string;
  service_price_register: number;
  service_price_recurring: number;
  prepaid: boolean;
  first_startdate: string;
  first_enddate: string;
  continue_startdate: string;
  continue_enddate: string;
  statement: string;
  /**
   * Per-product-type deep config, owned by the per-type template (Phase C/D).
   * The base flow leaves these empty; a template writes into them via RHF paths
   * `products.{i}.service_configoptions` / `.service_domain_metadata`.
   */
  service_configoptions: ContractServiceConfigOption[];
  service_domain_metadata: Record<string, unknown>;
}

/** One editable SLA rule row (response / resolution target per ticket group). */
export interface SlaRuleForm {
  ticket_type_group: string;
  response_time_minutes: number;
  resolution_time_minutes: number;
}

/**
 * The SLA config the form edits, revealed when `sla_enable`. Mirrors the backend
 * `ContractServiceSLAParams` — `template` picks a pre-defined tier (its metrics
 * auto-fill and stay editable), `custom` lets the user set them directly. Only
 * mapped into the payload when SLA is enabled.
 */
export interface ServiceSlaForm {
  sla_source: 'template' | 'custom';
  sla_template_id: number;
  uptime_guarantee: number;
  downtime_allowance_minutes: number;
  business_hours_only: boolean;
  business_hours_config: {
    timezone: string;
    work_days: number[];
    work_hours: { start: string; end: string };
  };
  sla_rules: SlaRuleForm[];
}

export interface ContractFormValues {
  // Parties
  customer_id: number;
  partner_id: number;
  customer_contact_id: number;
  /** Links the created contract back to a sales journey (from the deep-link URL). */
  customer_journey_id: number;
  // Dates
  contract_effective_date: string;
  contract_expiry_date: string;
  // Basic config
  sla_enable: boolean;
  nda_enable: boolean;
  use_contract_id: boolean;
  contract_id: number;
  currency_code: string;
  exchange_rate: number;
  contract_value: number;
  // Deep config (SLA / NDA)
  service_sla: ServiceSlaForm;
  nda_attachments: AttachmentPayload[];
  // Products
  products: ProductLine[];
  // Agreement
  contract_role: ContractRole;
  contract_direction: ContractDirection;
  contract_note: string;
}

/** `YYYY-MM-DD` for a date `yearsAhead` years / `daysOffset` days from today, LOCAL. */
export function localDate(yearsAhead = 0, daysOffset = 0): string {
  const d = new Date();
  if (yearsAhead) d.setFullYear(d.getFullYear() + yearsAhead);
  if (daysOffset) d.setDate(d.getDate() + daysOffset);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** An empty product line seeded with the contract term dates. */
export function emptyProduct(startdate = '', enddate = ''): ProductLine {
  return {
    product_group_name: '',
    product_id: 0,
    product_name: '',
    product_group_code: '',
    template_name: '',
    service_price_model: '',
    service_price_register: 0,
    service_price_recurring: 0,
    prepaid: false,
    first_startdate: startdate,
    first_enddate: enddate,
    continue_startdate: startdate,
    continue_enddate: enddate,
    statement: '',
    service_configoptions: [],
    service_domain_metadata: {},
  };
}

/** The default SLA config (legacy Gold-tier seed) shown when SLA is enabled. */
export function defaultServiceSla(): ServiceSlaForm {
  return {
    sla_source: 'template',
    sla_template_id: 0,
    uptime_guarantee: 99.9,
    downtime_allowance_minutes: 43,
    business_hours_only: true,
    business_hours_config: {
      timezone: 'Asia/Ho_Chi_Minh',
      work_days: [1, 2, 3, 4, 5],
      work_hours: { start: '08:00', end: '17:30' },
    },
    sla_rules: [
      { ticket_type_group: 'default', response_time_minutes: 30, resolution_time_minutes: 240 },
    ],
  };
}

/** Initial values — effective today, expiry one year minus a day (legacy default). */
export function defaultValues(): ContractFormValues {
  return {
    customer_id: 0,
    partner_id: 0,
    customer_contact_id: 0,
    customer_journey_id: 0,
    contract_effective_date: localDate(0, 0),
    contract_expiry_date: localDate(1, -1),
    sla_enable: false,
    nda_enable: false,
    use_contract_id: false,
    contract_id: 0,
    currency_code: 'VND',
    exchange_rate: 1,
    contract_value: 0,
    service_sla: defaultServiceSla(),
    nda_attachments: [],
    products: [],
    contract_role: 'customer',
    contract_direction: 'earn',
    contract_note: '',
  };
}

/** Currency options for the basic-config select (legacy set). */
export const CURRENCY_OPTIONS = [
  { value: 'VND', label: 'VND (₫)' },
  { value: 'USD', label: 'USD ($)' },
  { value: 'EUR', label: 'EUR (€)' },
  { value: 'JPY', label: 'JPY (¥)' },
];

const productSchema = yup.object({
  product_group_name: yup.string().required('Group is required'),
  product_id: yup.number().min(1, 'Select a product').required(),
  product_name: yup.string().required('Product is required'),
  product_group_code: yup.string().default(''),
  template_name: yup.string().default(''),
  service_price_model: yup.string().default(''),
  service_price_register: yup.number().min(0).defined().default(0),
  service_price_recurring: yup.number().min(0).defined().default(0),
  prepaid: yup.boolean().defined().default(false),
  first_startdate: yup.string().default(''),
  first_enddate: yup.string().default(''),
  continue_startdate: yup.string().default(''),
  continue_enddate: yup.string().default(''),
  statement: yup.string().default(''),
  // Deep per-type config — validated loosely here (the per-type template owns its
  // own shape); cast keeps the inferred schema aligned with ProductLine.
  service_configoptions: yup
    .array()
    .defined()
    .default([]) as unknown as yup.Schema<ContractServiceConfigOption[]>,
  service_domain_metadata: yup
    .object()
    .defined()
    .default({}) as unknown as yup.Schema<Record<string, unknown>>,
});

/**
 * Validation rules lifted from the legacy `ContractsNewSchema` core fields:
 * required customer / effective / expiry (expiry ≥ effective), role, direction,
 * and ≥1 product row each with group / id / name. The per-product-type
 * validators are later phases and intentionally not applied here.
 */
export const contractSchema: yup.ObjectSchema<ContractFormValues> = yup.object({
  customer_id: yup.number().min(1, 'Select a customer').required('Customer is required'),
  partner_id: yup.number().min(0).defined().default(0),
  customer_contact_id: yup.number().min(0).defined().default(0),
  customer_journey_id: yup.number().min(0).defined().default(0),
  contract_effective_date: yup.string().required('Effective date is required'),
  contract_expiry_date: yup
    .string()
    .required('Expiry date is required')
    .test('after-effective', 'Expiry date must be on or after the effective date', (value, ctx) => {
      const eff = ctx.parent.contract_effective_date as string;
      if (!value || !eff) return true;
      return value >= eff;
    }),
  sla_enable: yup.boolean().default(false),
  nda_enable: yup.boolean().default(false),
  use_contract_id: yup.boolean().default(false),
  contract_id: yup.number().min(0).defined().default(0),
  currency_code: yup.string().default('VND'),
  exchange_rate: yup.number().min(0).defined().default(1),
  contract_value: yup.number().min(0).defined().default(0),
  // Deep config — validated loosely (the SLA/NDA sections own their own shape);
  // the cast keeps the inferred schema aligned with ContractFormValues.
  service_sla: yup
    .object()
    .defined()
    .default(defaultServiceSla) as unknown as yup.Schema<ServiceSlaForm>,
  nda_attachments: yup
    .array()
    .defined()
    .default([]) as unknown as yup.Schema<AttachmentPayload[]>,
  products: yup.array().of(productSchema).min(1, 'Add at least one product').required(),
  contract_role: yup
    .mixed<ContractRole>()
    .oneOf(['customer', 'supplier', 'reseller', 'affiliate', 'collaborator'])
    .required('Select a role'),
  contract_direction: yup
    .mixed<ContractDirection>()
    .oneOf(['earn', 'pay'])
    .required('Select a direction'),
  contract_note: yup.string().default(''),
});

/** Coerce a possibly-empty numeric form value to a finite number (legacy `""→0`). */
function num(value: number | string | undefined | null): number {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Coerce a config option's numeric fields so empty inputs ("" / undefined) don't
 * reach the backend's `float64` commitment/price fields (which reject them with an
 * opaque 500). Mirrors legacy `reformat.ts` `buildServiceConfigoptions` + prices.
 */
function normalizeConfigOption(
  option: ContractServiceConfigOption,
): ContractServiceConfigOption {
  const out: ContractServiceConfigOption = {
    ...option,
    commitment_value: num(option.commitment_value),
    commitment_price_per_unit: num(option.commitment_price_per_unit),
  };
  if (Array.isArray(option.prices)) {
    out.prices = option.prices.map((price) => ({
      ...price,
      price_per_unit: num((price as Record<string, unknown>).price_per_unit as number),
      price_start: num((price as Record<string, unknown>).price_start as number),
      price_end: num((price as Record<string, unknown>).price_end as number),
    }));
  }
  return out;
}

/** Map a form product line → the `order.products[]` DTO. */
function toOrderProduct(p: ProductLine): ContractOrderProduct {
  const times: ContractServiceTimes = {
    first_startdate: p.first_startdate,
    first_enddate: p.first_enddate,
    continue_startdate: p.continue_startdate,
    continue_enddate: p.continue_enddate,
    statement: p.statement.trim() || undefined,
  };
  // OTHER/Other.Standard products carry no per-item options (legacy clears them).
  const isOther =
    p.template_name === 'Other.Standard' ||
    p.product_group_code?.toUpperCase?.() === 'OTHER';
  const configOptions = isOther
    ? []
    : (p.service_configoptions ?? []).map(normalizeConfigOption);
  return {
    product_id: p.product_id,
    product_name: p.product_name.trim(),
    product_group_name: p.product_group_name.trim(),
    product_group_code: p.product_group_code.trim() || undefined,
    template_name: isOther ? 'Other.Standard' : p.template_name.trim() || undefined,
    // `pricing_models` is a NOT-NULL enum ('package' | 'bundle' | 'percent_of_parent');
    // the backend casts this verbatim, so an empty string is a 500. Products don't
    // always carry a model, so default to 'package' (the DB default) — mirrors
    // legacy `product_price_model || PRICING_MODELS.Package`.
    service_price_model: p.service_price_model.trim() || 'package',
    service_price_register: num(p.service_price_register),
    service_price_recurring: num(p.service_price_recurring),
    prepaid: p.prepaid,
    service_configoptions: configOptions,
    service_domain_metadata: p.service_domain_metadata ?? {},
    service_times: times,
  };
}

/** Map the SLA form → the backend `service_sla` DTO (only when SLA is enabled). */
function toServiceSla(
  s: ServiceSlaForm,
  effectiveFrom: string,
  effectiveTo: string,
): ContractServiceSla {
  const fromTemplate = s.sla_source === 'template';
  return {
    sla_source: s.sla_source,
    sla_template_id: fromTemplate && s.sla_template_id > 0 ? s.sla_template_id : null,
    // The SLA period defaults to the contract term (legacy `buildServiceSLA`).
    effective_from: effectiveFrom,
    effective_to: effectiveTo,
    uptime_guarantee: num(s.uptime_guarantee),
    downtime_allowance_minutes: num(s.downtime_allowance_minutes),
    business_hours_only: s.business_hours_only,
    business_hours_config: s.business_hours_only ? s.business_hours_config : null,
    sla_rules: s.sla_rules.map((r) => ({
      ticket_type_group: r.ticket_type_group.trim() || 'default',
      response_time_minutes: num(r.response_time_minutes),
      resolution_time_minutes: num(r.resolution_time_minutes),
    })),
  };
}

/**
 * Map form values → the `POST /contracts/customers` payload (legacy
 * `reformatContractPayload`): `order_amount` = Σ register, dates already
 * `YYYY-MM-DD`, numerics coerced, `partner_id` null when ≤0. `service_sla` is
 * included only when `sla_enable`, `nda_attachments` only when `nda_enable`, and
 * `contract_id` only when `use_contract_id` links a parent contract.
 */
export function buildPayload(v: ContractFormValues): ContractCreatePayload {
  const products = v.products.map(toOrderProduct);
  const orderAmount = products.reduce((t, p) => t + num(p.service_price_register), 0);
  const payload: ContractCreatePayload = {
    use_contract_id: v.use_contract_id,
    partner_id: v.partner_id > 0 ? v.partner_id : null,
    customer_id: v.customer_id,
    customer_contact_id: v.customer_contact_id > 0 ? v.customer_contact_id : null,
    contract_effective_date: v.contract_effective_date,
    contract_expiry_date: v.contract_expiry_date,
    contract_value: num(v.contract_value),
    contract_role: v.contract_role,
    contract_direction: v.contract_direction,
    contract_note: v.contract_note.trim(),
    sla_enable: v.sla_enable,
    nda_enable: v.nda_enable,
    currency_code: v.currency_code || 'VND',
    exchange_rate: num(v.exchange_rate) || 1,
    order: {
      order_source: 'dc',
      order_source_name: 'customer',
      order_note: '',
      order_amount: orderAmount,
      products,
    },
  };
  if (v.customer_journey_id > 0) payload.customer_journey_id = v.customer_journey_id;
  if (v.use_contract_id && v.contract_id > 0) payload.contract_id = v.contract_id;
  if (v.sla_enable) {
    payload.service_sla = toServiceSla(
      v.service_sla,
      v.contract_effective_date,
      v.contract_expiry_date,
    );
  }
  if (v.nda_enable) payload.nda_attachments = v.nda_attachments;
  return payload;
}
